import type {
  IYeuCauXuatHoaDon,
  IYeuCauXuatHoaDonChiTiet,
  IYeuCauXuatHoaDonHoaDon,
  IYeuCauXuatHoaDonThuKhac,
  YcxTrangThai,
  YeuCauXuatHoaDonAction,
} from '@/interfaces/yeuCauXuatHoaDonInterfaces'
import { YEU_CAU_XUAT_HOA_DON_PERMISSION_CODES as P } from '@/constants/chucNangs/yeuCauXuatHoaDonChucNangs'
import { maGocTuHangHoa } from '@/utils/maHangHienThi'
const YCX_VALID_STATES: ReadonlySet<YcxTrangThai> = new Set<YcxTrangThai>([
  'MOI_TAO',
  'CHO_KT_XU_LY',
  'CHO_KHACH_DUYET',
  'KHACH_DONG_Y',
  'HOAN_THANH',
  'DA_HUY',
])

function normalizeTrangThai(raw: unknown): YcxTrangThai {
  const v = typeof raw === 'string' ? raw : String(raw ?? '')
  return YCX_VALID_STATES.has(v as YcxTrangThai) ? (v as YcxTrangThai) : 'MOI_TAO'
}

function toStr(v: unknown): string | null {
  return v != null ? String(v) : null
}

function toNum(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (v != null && v !== '') {
    const n = Number(v)
    if (Number.isFinite(n)) return n
  }
  return 0
}

function mapChiTietRows(rows: Record<string, unknown>[]): IYeuCauXuatHoaDonChiTiet[] {
  return rows.map((ct) => {
    const hangHoa = ct.hangHoa as Record<string, unknown> | null | undefined
    return {
      id: toStr(ct.id) ?? undefined,
      hangHoaId: String(ct.hangHoaId ?? ''),
      hangHoa:
        hangHoa && typeof hangHoa === 'object'
          ? {
              id: String(hangHoa.id ?? ''),
              tenHangHoa: toStr(hangHoa.tenHangHoa),
              maHangHoa: toStr(hangHoa.maHangHoa),
              hangHoaGoc: maGocTuHangHoa(hangHoa) ? { maHangHoa: maGocTuHangHoa(hangHoa) } : null,
            }
          : null,
      soLuong: toNum(ct.soLuong),
      donGia: toStr(ct.donGia),
      giamGia: toStr(ct.giamGia),
      giamGiaPhanTram: toStr(ct.giamGiaPhanTram),
      giaBan: toStr(ct.giaBan),
      thanhTien: toStr(ct.thanhTien),
      donViTinh: toStr(ct.donViTinh),
      ghiChu: toStr(ct.ghiChu),
      maSoThue: toStr(ct.maSoThue),
      tenCongTyLayThue: toStr(ct.tenCongTyLayThue),
      diaChiLayThue: toStr(ct.diaChiLayThue),
      soDienThoai: toStr(ct.soDienThoai),
      email: toStr(ct.email),
      soCCCD: toStr(ct.soCCCD),
      soTaiKhoanNganHang: toStr(ct.soTaiKhoanNganHang),
      nganHang: toStr(ct.nganHang),
      maHoaDonNguon: toStr(ct.maHoaDonNguon),
      tenKhachHang: toStr(ct.tenKhachHang),
      thongTinVatHangHoaId: toStr(ct.thongTinVatHangHoaId),
      ngayKhaiVat: toStr(ct.ngayKhaiVat),
      tenKhaiVat: toStr(ct.tenKhaiVat),
      donViTinhKhaiVat: toStr(ct.donViTinhKhaiVat),
      tyLeQuyDoi: ct.tyLeQuyDoi != null && ct.tyLeQuyDoi !== '' ? toNum(ct.tyLeQuyDoi) : null,
      donGiaKhaiVat: ct.donGiaKhaiVat != null && ct.donGiaKhaiVat !== '' ? toStr(ct.donGiaKhaiVat) : null,
      soLuongTonKhaiVat:
        ct.soLuongTonKhaiVat != null && ct.soLuongTonKhaiVat !== '' ? toStr(ct.soLuongTonKhaiVat) : null,
      phanTramVat: ct.phanTramVat != null && ct.phanTramVat !== '' ? toStr(ct.phanTramVat) : null,
      thuTu: ct.thuTu != null ? toNum(ct.thuTu) : undefined,
    }
  })
}

function mapThuKhacRows(rows: Record<string, unknown>[]): IYeuCauXuatHoaDonThuKhac[] {
  return rows.map((tk) => {
    const danhMucThuKhac = tk.danhMucThuKhac as Record<string, unknown> | null | undefined
    return {
      id: toStr(tk.id) ?? undefined,
      maThuKhac: toStr(tk.maThuKhac),
      tenThuKhac: String(tk.tenThuKhac ?? ''),
      giaTri: toStr(tk.giaTri),
      phanTram: toStr(tk.phanTram),
      ghiChu: toStr(tk.ghiChu),
      danhmucThuKhacId: toStr(tk.danhmucThuKhacId ?? tk.danhMucThuKhacId),
      danhMucThuKhac:
        danhMucThuKhac && typeof danhMucThuKhac === 'object'
          ? {
              id: String(danhMucThuKhac.id ?? ''),
              tenThuKhac: toStr(danhMucThuKhac.tenThuKhac),
            }
          : null,
    }
  })
}

function mapHoaDonXuat(raw: Record<string, unknown>): IYeuCauXuatHoaDonHoaDon {
  const chiTietRaw = Array.isArray(raw.chiTiet) ? (raw.chiTiet as Record<string, unknown>[]) : []
  const thuKhacRaw = Array.isArray(raw.thuKhacItems)
    ? (raw.thuKhacItems as Record<string, unknown>[])
    : []
  return {
    id: String(raw.id ?? ''),
    thuTu: raw.thuTu != null ? toNum(raw.thuTu) : undefined,
    thongTinXuatHoaDonId: toStr(raw.thongTinXuatHoaDonId),
    tenNguoiMua: toStr(raw.tenNguoiMua),
    maSoThue: toStr(raw.maSoThue),
    diaChiHoaDon: toStr(raw.diaChiHoaDon),
    tinhThanhHoaDon: toStr(raw.tinhThanhHoaDon),
    phuongXaHoaDon: toStr(raw.phuongXaHoaDon),
    soCCCD: toStr(raw.soCCCD),
    soHoChieu: toStr(raw.soHoChieu),
    emailHoaDon: toStr(raw.emailHoaDon),
    soDienThoaiHoaDon: toStr(raw.soDienThoaiHoaDon),
    nganHang: toStr(raw.nganHang),
    soTaiKhoanNganHang: toStr(raw.soTaiKhoanNganHang),
    tongTienHang: toStr(raw.tongTienHang),
    giamGia: toStr(raw.giamGia),
    giamGiaPhanTram: toStr(raw.giamGiaPhanTram),
    tongSauGiamGia: toStr(raw.tongSauGiamGia),
    tongThuKhac: toStr(raw.tongThuKhac ?? raw.thuKhac),
    khachCanTra: toStr(raw.khachCanTra),
    khachDaTra: toStr(raw.khachDaTra),
    tienThua: toStr(raw.tienThua),
    thueSuat: toStr(raw.thueSuat),
    tienThue: toStr(raw.tienThue),
    soHoaDon: toStr(raw.soHoaDon),
    kyHieuHoaDon: toStr(raw.kyHieuHoaDon),
    ngayXuatHoaDon: toStr(raw.ngayXuatHoaDon),
    chiTiet: mapChiTietRows(chiTietRaw),
    thuKhacItems: mapThuKhacRows(thuKhacRaw),
  }
}

/** Defensive mapping raw API payload → IYeuCauXuatHoaDon (clone khachHangApi.mapKhachHangFromApi). */
export function mapYeuCauFromApi(raw: Record<string, unknown>): IYeuCauXuatHoaDon {
  const hoaDonGoc = raw.hoaDonGoc as Record<string, unknown> | null | undefined
  const nguoiTao = raw.nguoiTao as Record<string, unknown> | null | undefined
  const nguoiCapNhat = raw.nguoiCapNhat as Record<string, unknown> | null | undefined
  const chiTietRaw = Array.isArray(raw.chiTiet) ? (raw.chiTiet as Record<string, unknown>[]) : []
  const thuKhacRaw = Array.isArray(raw.thuKhacItems)
    ? (raw.thuKhacItems as Record<string, unknown>[])
    : []
  const khachHang = raw.khachHang as Record<string, unknown> | null | undefined
  const hoaDonsRaw = Array.isArray(raw.hoaDons) ? (raw.hoaDons as Record<string, unknown>[]) : []
  const rootCount = toNum((raw._count as { chiTiet?: unknown } | null | undefined)?.chiTiet)
  const hoaDonCount = hoaDonsRaw.reduce((sum, hd) => {
    const c = toNum((hd._count as { chiTiet?: unknown } | null | undefined)?.chiTiet)
    return sum + c
  }, 0)
  const chiTietLen = Array.isArray(raw.chiTiet) ? raw.chiTiet.length : 0
  const hoaDonChiTietLen = hoaDonsRaw.reduce((sum, hd) => {
    return sum + (Array.isArray(hd.chiTiet) ? hd.chiTiet.length : 0)
  }, 0)
  // max, không cộng: dòng hàng vừa thuộc yêu cầu vừa thuộc hóa đơn con.
  const soMatHang = Math.max(
    toNum(raw.soMatHang),
    hoaDonCount,
    rootCount,
    chiTietLen,
    hoaDonChiTietLen,
  )
  return {
    id: String(raw.id ?? ''),
    maYeuCau: String(raw.maYeuCau ?? ''),
    khachHangId: toStr(raw.khachHangId),
    khachHang:
      khachHang && typeof khachHang === 'object'
        ? (() => {
            const nv = khachHang.nhanVienPhuTrach as Record<string, unknown> | null | undefined
            return {
              id: String(khachHang.id ?? ''),
              maKhachHang: toStr(khachHang.maKhachHang),
              tenKhachHang: toStr(khachHang.tenKhachHang),
              nhanVienPhuTrach:
                nv && typeof nv === 'object'
                  ? { id: toNum(nv.id), username: toStr(nv.username) }
                  : null,
            }
          })()
        : null,
    hoaDonGocId: String(raw.hoaDonGocId ?? ''),
    hoaDonGoc:
      hoaDonGoc && typeof hoaDonGoc === 'object'
        ? { id: String(hoaDonGoc.id ?? ''), maHoaDon: String(hoaDonGoc.maHoaDon ?? '') }
        : null,
    hoaDonGocIds: Array.isArray(raw.hoaDonGocIds)
      ? (raw.hoaDonGocIds as unknown[]).map((id) => String(id))
      : [],
    hoaDonGocs: Array.isArray(raw.hoaDonGocs)
      ? (raw.hoaDonGocs as Record<string, unknown>[]).map((hd) => ({
          id: String(hd.id ?? ''),
          maHoaDon: String(hd.maHoaDon ?? ''),
        }))
      : [],
    trangThai: normalizeTrangThai(raw.trangThai),
    lyDoXuat: String(raw.lyDoXuat ?? ''),
    lyDoHuy: toStr(raw.lyDoHuy),
    lyDoReject: toStr(raw.lyDoReject),
    version: toNum(raw.version),
    soLanReject: toNum(raw.soLanReject),
    tenNguoiMua: toStr(raw.tenNguoiMua),
    maSoThue: toStr(raw.maSoThue),
    diaChiHoaDon: toStr(raw.diaChiHoaDon),
    tinhThanhHoaDon: toStr(raw.tinhThanhHoaDon),
    phuongXaHoaDon: toStr(raw.phuongXaHoaDon),
    soCCCD: toStr(raw.soCCCD),
    soHoChieu: toStr(raw.soHoChieu),
    emailHoaDon: toStr(raw.emailHoaDon),
    soDienThoaiHoaDon: toStr(raw.soDienThoaiHoaDon),
    nganHang: toStr(raw.nganHang),
    soTaiKhoanNganHang: toStr(raw.soTaiKhoanNganHang),
    tongTienHang: toStr(raw.tongTienHang),
    giamGia: toStr(raw.giamGia),
    giamGiaPhanTram: toStr(raw.giamGiaPhanTram),
    tongSauGiamGia: toStr(raw.tongSauGiamGia),
    // BE lưu field `thuKhac` (Prisma) — fallback cả 2 tên cho an toàn.
    tongThuKhac: toStr(raw.tongThuKhac ?? raw.thuKhac),
    khachCanTra: toStr(raw.khachCanTra),
    khachDaTra: toStr(raw.khachDaTra),
    tienThua: toStr(raw.tienThua),
    phiGiaoHang: toStr(raw.phiGiaoHang),
    tienCod: toStr(raw.tienCod),
    thueSuat: toStr(raw.thueSuat),
    tienThue: toStr(raw.tienThue),
    chiTiet: mapChiTietRows(chiTietRaw),
    thuKhacItems: mapThuKhacRows(thuKhacRaw),
    hoaDons: hoaDonsRaw.map(mapHoaDonXuat),
    soMatHang,
    thoiGianGuiKT: toStr(raw.thoiGianGuiKT),
    thoiGianGuiKhach: toStr(raw.thoiGianGuiKhach),
    thoiGianHoanThanh: toStr(raw.thoiGianHoanThanh),
    thoiGianHuy: toStr(raw.thoiGianHuy),
    duAnId: toNum(raw.duAnId),
    chiNhanhId: toNum(raw.chiNhanhId),
    nguoiTaoId: raw.nguoiTaoId != null ? toNum(raw.nguoiTaoId) : null,
    nguoiTao:
      nguoiTao && typeof nguoiTao === 'object'
        ? {
            id: toNum(nguoiTao.id),
            username: toStr(nguoiTao.username),
          }
        : null,
    nguoiCapNhatId: raw.nguoiCapNhatId != null ? toNum(raw.nguoiCapNhatId) : null,
    nguoiCapNhat:
      nguoiCapNhat && typeof nguoiCapNhat === 'object'
        ? {
            id: toNum(nguoiCapNhat.id),
            username: toStr(nguoiCapNhat.username),
          }
        : null,
    ngayTao: String(raw.ngayTao ?? ''),
    ngayCapNhat: String(raw.ngayCapNhat ?? ''),
  }
}

/** D-03 transition matrix — FROM_STATES cho mỗi action (mirror BE state machine). */
const YCX_FROM_STATES: Record<YeuCauXuatHoaDonAction, YcxTrangThai[]> = {
  GUI_KT: ['MOI_TAO'],
  GUI_KHACH: ['CHO_KT_XU_LY'],
  KHACH_DONG_Y: ['CHO_KHACH_DUYET'],
  KHACH_KHONG_DONG_Y: ['CHO_KHACH_DUYET'],
  HOAN_THANH: ['KHACH_DONG_Y'],
  HUY: ['MOI_TAO', 'CHO_KT_XU_LY', 'CHO_KHACH_DUYET', 'KHACH_DONG_Y'],
}

/** Permission code yêu cầu cho mỗi action (mirror BE requiredRole — D-03). */
const YCX_ACTION_PERMISSION: Record<YeuCauXuatHoaDonAction, string> = {
  GUI_KT: P.WEB_BH_YCX_EDIT,
  GUI_KHACH: P.WEB_BH_YCX_GUI_KHACH,
  KHACH_DONG_Y: P.WEB_BH_YCX_DONG_Y,
  KHACH_KHONG_DONG_Y: P.WEB_BH_YCX_KHONG_DONG_Y,
  HOAN_THANH: P.WEB_BH_YCX_HOAN_THANH,
  HUY: P.WEB_BH_YCX_HUY,
}

/**
 * FE transition gate — kiểm tra trạng thái nguồn + permission code.
 * Dùng cho TransitionBar render (filter button theo role+state hiện tại).
 * Lưu ý: đây là UX gate; BE re-check permission trong state machine (T-03-01).
 */
export function canTransition(
  trangThai: YcxTrangThai,
  action: YeuCauXuatHoaDonAction,
  hasPermission: (code: string) => boolean,
): boolean {
  const allowedFrom = YCX_FROM_STATES[action]
  if (!allowedFrom.includes(trangThai)) return false
  const required = YCX_ACTION_PERMISSION[action]
  if (hasPermission(required)) return true
  // HUY: NVBH thường có EDIT nhưng thiếu riêng WEB_BH_YCX_HUY.
  if (action === 'HUY' && hasPermission(P.WEB_BH_YCX_EDIT)) return true
  return false
}
